import React, { useState, useEffect } from 'react'
import { Tooltip, Spin } from 'antd'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { segmentApi } from '@edulastic/api'
import {
  IconPDLSparkle,
  IconInfoCircle,
  IconPearDeck,
  IconOpenBookFilled,
  IconChevronLeft,
} from '@edulastic/icons'
import { storeClassStudentAssignmentMetadata } from '@edulastic/api/src/utils/Storage'
import { FlexContainer } from '@edulastic/common'
import { PEAR_PRODUCT } from '@edulastic/constants/const/pear'
import {
  FollowUpsSection,
  RecommendationsTitle,
  GenerateFromScratchButton,
  FollowUpCardsContainer,
  RecommendationsCarouselRow,
  CarouselCards,
  CarouselArrowButton,
  CarouselPageIndicator,
  FollowUpCard,
  FollowUpCardHeader,
  FollowUpCardIcon,
  FollowUpCardTitle,
  FollowUpCardMeta,
  FollowUpPlaceholderCard,
  FollowupNameAndIcon,
  LowestBandRecommendationBox,
  LowestBandRecommendationContent,
  LowestBandRecommendationText,
  LowestBandRecommendationBold,
  CreateAccountButton,
  ExternalLinkIconBlack,
} from './styled'
import ILPDrawer from '../../../AssessmentCreate/components/CreateAITest/ILPDrawer'
import {
  getPDLDefaultGrade,
  getPDLGradeId,
} from '../../../AssessmentCreate/components/CreateAITest/ducks/helpers'
import { getILPTransformPropData } from '../../../StandardsBasedReport/components/CustomContentGeneration/utils'
import {
  getTestDataSelector,
  getAdditionalDataSelector,
} from '../../../ClassBoard/ducks'
import {
  getUserIdSelector,
  getUserOrgId,
  getUserEmailSelector,
  isGcpsDistrictSelector,
} from '../../selectors/user'
import { getHasPearDeckAccessSelector } from '../../../TestList/ducks'
import {
  FOLLOW_UP_TYPE,
  FOLLOW_UP_TYPE_TO_TITLE,
  ILP_CONTENT_TYPES,
  DIFFICULTY_LEVELS,
  DOK_LEVELS,
  GROUP_AND_ASSIGN_RECOMMENDATIONS_PER_PAGE,
} from './constants'
import {
  buildGroupAndAssignTestLibraryPath,
  buildGroupAndAssignContentOrchardUrl,
} from './groupAndAssignLibraryLinks'

const FOLLOW_UP_WIDE_BUTTON_STYLE = {
  marginBottom: 20,
  width: '100%',
  justifyContent: 'center',
}

/** Standard subgroup fields for Segment interaction payloads. */
const getStandardsSegmentPayload = (standardGroup) => {
  if (!standardGroup) return undefined
  return [
    {
      standardId: standardGroup._id,
      identifier: standardGroup.identifier,
      pdlId: standardGroup.pdlId,
    },
  ]
}

const RecommendedFollowUps = ({
  followUps = [],
  subGroup,
  testData,
  assignmentAdditionalData,
  userId,
  districtId,
  isMastered,
  isLowestBand = false,
  hasPearDeckAccess = false,
  isGcpsDistrict = false,
  recommendedTestsLoading,
  recommendedDecksLoading,
  userEmail = '',
  /** ClassBoard entry only — enables ILP `transform` from assignment test context. */
  includeTestContext = false,
  defaultGrades = [],
  defaultSubjects = [],
  /** Standard set (curriculum) for pre-filling PA test library filters. */
  curriculumId = null,
}) => {
  const [isILPVisible, setIsILPVisible] = useState(false)
  const [carouselPage, setCarouselPage] = useState(0)

  const perPage = GROUP_AND_ASSIGN_RECOMMENDATIONS_PER_PAGE
  const totalPages = Math.max(1, Math.ceil(followUps.length / perPage))
  const clampedPage = Math.min(carouselPage, totalPages - 1)
  const pageStart = clampedPage * perPage
  const visibleFollowUps = followUps.slice(pageStart, pageStart + perPage)

  // Reset to the first page whenever the recommendation set changes.
  useEffect(() => {
    setCarouselPage(0)
  }, [followUps.length])

  const handleCreatePearDeckAccount = () => {
    segmentApi.genericEventTrack(
      'GroupAndAssign:CreatePearDeckAccountClick',
      {}
    )
    if (
      typeof window.pear?.identity?.openInitializeProductModal === 'function' &&
      userEmail
    ) {
      window.pear.identity.openInitializeProductModal({
        email: userEmail,
        product: PEAR_PRODUCT.PEAR_DECK,
        followRedirectInModal: true,
      })
    }
  }

  const handleGenerateFromScratch = () => {
    segmentApi.genericEventTrack('GroupAndAssign:GenerateWithAIClick', {
      standards: getStandardsSegmentPayload(subGroup),
      title: 'Generate with AI',
    })
    setIsILPVisible(true)
  }

  const handleOpenTestLibrary = () => {
    segmentApi.genericEventTrack('GroupAndAssign:TestLibraryClick', {
      standards: getStandardsSegmentPayload(subGroup),
      title: 'Test library',
    })
    const path = buildGroupAndAssignTestLibraryPath({
      curriculumId,
      grades: defaultGrades,
      subjects: defaultSubjects,
      standardGroup: subGroup,
    })
    window.open(`${window.location.origin}${path}`, '_blank')
  }

  const getPearDeckLaunchContext = async () => {
    if (!window.pear?.identity?.getIdentity) {
      return { signInRedirectUrl: null, origin: null }
    }
    try {
      const identity = await window.pear.identity.getIdentity()
      const pearDeckAccess = identity?.product_access_data?.find(
        (p) => p.product === PEAR_PRODUCT.PEAR_DECK
      )
      const signInRedirectUrl = pearDeckAccess?.redirect_url || null
      let origin = null
      if (signInRedirectUrl) {
        try {
          origin = new URL(signInRedirectUrl).origin
        } catch (e) {
          origin = null
        }
      }
      return { signInRedirectUrl, origin }
    } catch (e) {
      return { signInRedirectUrl: null, origin: null }
    }
  }

  const openWithPearSignInRedirect = (signInRedirectUrl, destinationUrl) => {
    if (!signInRedirectUrl || !destinationUrl) return false
    const separator = signInRedirectUrl.includes('?') ? '&' : '?'
    window.open(
      `${signInRedirectUrl}${separator}redirectTo=${encodeURIComponent(
        destinationUrl
      )}`,
      '_blank'
    )
    return true
  }

  /** Opens a Pear Deck / PD URL via SSO redirect when available (recommendation deck cards). */
  const openPearDeckDestination = async (destinationUrl) => {
    if (!destinationUrl) return
    const { signInRedirectUrl } = await getPearDeckLaunchContext()
    if (!openWithPearSignInRedirect(signInRedirectUrl, destinationUrl)) {
      window.open(destinationUrl, '_blank')
    }
  }

  const handleOpenDeckLibrary = async () => {
    segmentApi.genericEventTrack('GroupAndAssign:DeckLibraryClick', {
      standards: getStandardsSegmentPayload(subGroup),
      title: 'Deck library',
    })
    const { signInRedirectUrl, origin } = await getPearDeckLaunchContext()
    if (!origin) return
    const destinationUrl = buildGroupAndAssignContentOrchardUrl({
      baseOrigin: origin,
      grades: defaultGrades,
      subjects: defaultSubjects,
      standardGroup: subGroup,
    })
    if (!destinationUrl) return
    if (!openWithPearSignInRedirect(signInRedirectUrl, destinationUrl)) {
      window.open(destinationUrl, '_blank')
    }
  }

  // Transform sub-group students to the format expected by ILP
  const selectedStudents = (subGroup?.students || []).map((student) => {
    const standard = {
      pdlId: subGroup?.pdlId,
      identifier: subGroup?.identifier,
      perfomancePercentage: student.scorePercentage || 0,
    }

    return {
      studentId: student.id || student._id || student.studentId,
      studentName: student.name || student.studentName,
      standards: [standard],
    }
  })

  const handleLaunch = async (followUp, rank) => {
    const baseUrl = window.location.origin
    const isAssessment = followUp.type === FOLLOW_UP_TYPE.PEAR_ASSESSMENT_TEST
    const isDeck = followUp.type === FOLLOW_UP_TYPE.PEAR_DECK
    const contentUrl = isAssessment
      ? `${baseUrl}/author/tests/tab/review/id/${followUp._id}`
      : isDeck
      ? followUp.previewURL || followUp.url || ''
      : undefined

    segmentApi.genericEventTrack('GroupAndAssign:RecommendationClick', {
      type: followUp.type,
      title: followUp.title,
      // 1-based position in the ranked list — measured to improve ranking.
      rank,
      standards: getStandardsSegmentPayload(subGroup),
      url: contentUrl || undefined,
    })
    if (isAssessment) {
      window.open(contentUrl, '_blank')
    } else if (isDeck) {
      await openPearDeckDestination(contentUrl)
    }
  }

  // Generate with AI must use the standard's grade level, not the test's.
  // `defaultGrades` is the standard grade (set from `standardGroup.grades` in GroupAccordion);
  // override the test-derived `gradeLevelId` that getILPTransformPropData returns.
  const standardGradeLevelId = getPDLGradeId(getPDLDefaultGrade(defaultGrades))
  const classBoardILPTransform = includeTestContext
    ? {
        ...getILPTransformPropData({
          testData: testData || {},
          selectedStudents,
        }),
        ...(standardGradeLevelId ? { gradeLevelId: standardGradeLevelId } : {}),
      }
    : null

  // Pear Deck reteach content requires an active PD subscription (GCPS never has one).
  // When PD isn't available we fall back to Pear Assessment for the non-mastered bands where
  // Pear Deck would otherwise be the default.
  const pdAvailable = hasPearDeckAccess && !isGcpsDistrict

  // Preselect ILP flow by mastery band: lowest → Pear Deck only;
  // below mastery but not lowest → Pear Deck + Practice; above mastery → Practice + Assessment.
  // Without PD access, the Pear Deck reteach slot is swapped for a Pear Assessment test (Practice kept).
  const defaultContentTypes = (() => {
    if (isMastered) {
      return [
        ILP_CONTENT_TYPES.PEAR_PRACTICE,
        ILP_CONTENT_TYPES.PEAR_ASSESSMENT_TEST,
      ]
    }
    const reteachContentType = pdAvailable
      ? ILP_CONTENT_TYPES.PEAR_DECK
      : ILP_CONTENT_TYPES.PEAR_ASSESSMENT_TEST
    if (isLowestBand) {
      return [reteachContentType]
    }
    return [reteachContentType, ILP_CONTENT_TYPES.PEAR_PRACTICE]
  })()

  const pearAssessmentTestConfig = (() => {
    if (isMastered) {
      return {
        difficulty: DIFFICULTY_LEVELS.DIFFICULTY_HARD,
        dok: DOK_LEVELS.DOK_3_STRATEGIC_THINKING,
      }
    }
    // Lowest band, or the no-PD Pear Assessment fallback, generate an easy recall test.
    if (isLowestBand || !pdAvailable) {
      return {
        difficulty: DIFFICULTY_LEVELS.DIFFICULTY_EASY,
        dok: DOK_LEVELS.DOK_1_RECALL,
      }
    }
    return {
      difficulty: DIFFICULTY_LEVELS.DIFFICULTY_MEDIUM,
      dok: DOK_LEVELS.DOK_2_SKILL_CONCEPT,
    }
  })()

  const onClickILPGeneratedContent = (generatedContentMetadata) => {
    const {
      generated_content_type,
      status,
      generated_content_url,
      generated_content_id,
    } = generatedContentMetadata

    if (
      status !== 'STATUS_ITEM_COMPLETE' ||
      generated_content_type !== 'TYPE_PEAR_ASSESSMENT_TEST'
    ) {
      return false
    }

    const authorIndex = generated_content_url.indexOf('author')
    if (authorIndex === -1) {
      return false
    }

    const testReviewpageUrl = generated_content_url.substring(authorIndex)
    const selectedStudentsIds = selectedStudents.map(
      ({ studentId }) => studentId
    )

    if (assignmentAdditionalData?.classId && userId && districtId) {
      storeClassStudentAssignmentMetadata({
        selectedStudentsIds,
        classId: assignmentAdditionalData.classId,
        testId: generated_content_id,
        userId,
        districtId,
      })
    }

    const baseUrl = window.location.origin
    window.open(`${baseUrl}/${testReviewpageUrl}`, '_blank')

    return true
  }

  const libraryCta =
    !isMastered && pdAvailable
      ? { label: 'Deck library', onClick: handleOpenDeckLibrary }
      : { label: 'Test library', onClick: handleOpenTestLibrary }

  const libraryButton = (
    <GenerateFromScratchButton
      type="button"
      onClick={libraryCta.onClick}
      style={FOLLOW_UP_WIDE_BUTTON_STYLE}
    >
      <IconOpenBookFilled width={18} height={18} />
      {libraryCta.label}
      <ExternalLinkIconBlack
        width={14}
        height={14}
        style={{ marginLeft: 6 }}
        title="Opens in new tab"
      />
    </GenerateFromScratchButton>
  )

  return (
    <FollowUpsSection>
      <FlexContainer
        justifyContent="space-between"
        alignItems="center"
        marginBottom="12px"
      >
        <RecommendationsTitle>Recommendations</RecommendationsTitle>
        {!recommendedTestsLoading &&
          !recommendedDecksLoading &&
          followUps.length > perPage && (
            <CarouselPageIndicator>
              Page {clampedPage + 1}/{totalPages}
            </CarouselPageIndicator>
          )}
      </FlexContainer>
      {isILPVisible && (
        <ILPDrawer
          visible={isILPVisible}
          setIsILPVisible={setIsILPVisible}
          contentTypes={defaultContentTypes}
          pearAssessmentTestConfig={pearAssessmentTestConfig}
          {...(classBoardILPTransform != null
            ? { transform: classBoardILPTransform }
            : {
                defaultGrades,
                defaultSubjects,
              })}
          onClickContentLink={onClickILPGeneratedContent}
        />
      )}

      {!isMastered && followUps.length === 0 && !pdAvailable ? (
        <>
          {/* GCPS never creates a Pear Deck account, so skip the reteach-account nudge. */}
          {!isGcpsDistrict && (
            <LowestBandRecommendationBox>
              <LowestBandRecommendationContent>
                <IconInfoCircle
                  fillColor="#007ABD"
                  width={20}
                  height={20}
                  style={{ flexShrink: 0 }}
                />
                <LowestBandRecommendationText>
                  <p>
                    <LowestBandRecommendationBold>
                      Get recommended reteach lessons
                    </LowestBandRecommendationBold>
                  </p>
                  <p>Create a free account to get started.</p>
                </LowestBandRecommendationText>
              </LowestBandRecommendationContent>
              <CreateAccountButton
                type="button"
                onClick={handleCreatePearDeckAccount}
              >
                <IconPearDeck width={20} height={20} />
                Create a Pear Deck account
              </CreateAccountButton>
            </LowestBandRecommendationBox>
          )}
          <GenerateFromScratchButton
            onClick={handleGenerateFromScratch}
            style={FOLLOW_UP_WIDE_BUTTON_STYLE}
          >
            <IconPDLSparkle width={18} height={18} />
            Generate with AI
            <ExternalLinkIconBlack
              width={14}
              height={14}
              style={{ marginLeft: 6 }}
              title="Opens in new tab"
            />
          </GenerateFromScratchButton>
          {libraryButton}
        </>
      ) : (
        <>
          {(isMastered && recommendedTestsLoading) ||
          (!isMastered && recommendedDecksLoading) ? (
            <FollowUpCardsContainer>
              <FollowUpPlaceholderCard fullWidth>
                <span className="loading-text">
                  <Spin size="small" />
                </span>
              </FollowUpPlaceholderCard>
            </FollowUpCardsContainer>
          ) : followUps.length > 0 ? (
            <RecommendationsCarouselRow>
              <CarouselArrowButton
                type="button"
                disabled={clampedPage === 0}
                onClick={() => setCarouselPage((p) => Math.max(0, p - 1))}
                aria-label="Previous recommendations"
              >
                <IconChevronLeft width={14} height={14} />
              </CarouselArrowButton>
              <CarouselCards>
                {visibleFollowUps.map((followUp, i) => {
                  const rank = pageStart + i + 1
                  return (
                    <FollowUpCard
                      key={followUp._id || followUp.id || rank}
                      onClick={() => handleLaunch(followUp, rank)}
                    >
                      <FollowUpCardHeader>
                        <FollowUpCardIcon>{followUp.icon}</FollowUpCardIcon>
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: 'bold',
                          }}
                        >
                          {followUp.title}
                        </span>
                        <ExternalLinkIconBlack
                          width={14}
                          height={14}
                          style={{ flexShrink: 0 }}
                          title="Opens in new tab"
                        />
                      </FollowUpCardHeader>
                      <FollowUpCardMeta>
                        <FollowupNameAndIcon>
                          <Tooltip
                            title={FOLLOW_UP_TYPE_TO_TITLE[followUp.type]}
                          >
                            <FollowUpCardTitle>
                              {FOLLOW_UP_TYPE_TO_TITLE[followUp.type]}
                            </FollowUpCardTitle>
                          </Tooltip>
                        </FollowupNameAndIcon>
                        <div>
                          <p>{followUp.itemsCountText}</p>
                        </div>
                      </FollowUpCardMeta>
                    </FollowUpCard>
                  )
                })}
              </CarouselCards>
              <CarouselArrowButton
                type="button"
                disabled={clampedPage >= totalPages - 1}
                onClick={() =>
                  setCarouselPage((p) => Math.min(totalPages - 1, p + 1))
                }
                aria-label="Next recommendations"
              >
                <IconChevronLeft
                  width={14}
                  height={14}
                  style={{ transform: 'rotate(180deg)' }}
                />
              </CarouselArrowButton>
            </RecommendationsCarouselRow>
          ) : null}
          {!recommendedTestsLoading && !recommendedDecksLoading && (
            <>
              <GenerateFromScratchButton
                onClick={handleGenerateFromScratch}
                style={FOLLOW_UP_WIDE_BUTTON_STYLE}
              >
                <IconPDLSparkle width={18} height={18} />
                Generate with AI
                <ExternalLinkIconBlack
                  width={14}
                  height={14}
                  style={{ marginLeft: 6 }}
                  title="Opens in new tab"
                />
              </GenerateFromScratchButton>
              {libraryButton}
            </>
          )}
        </>
      )}
    </FollowUpsSection>
  )
}

const enhance = compose(
  connect(
    (state) => ({
      testData: getTestDataSelector(state),
      assignmentAdditionalData: getAdditionalDataSelector(state),
      userId: getUserIdSelector(state),
      districtId: getUserOrgId(state),
      hasPearDeckAccess: getHasPearDeckAccessSelector(state),
      // Include GCPS demo/test districts so they get the same test-only behaviour (per PM).
      isGcpsDistrict: isGcpsDistrictSelector(state, false, true),
      userEmail: getUserEmailSelector(state),
    }),
    null
  )
)

export default enhance(RecommendedFollowUps)
